import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import {
  Modal,
  Form,
  DatePicker,
  TimePicker,
  Input,
  Button,
  message,
  Typography,
  Spin,
  Flex,
  Table,
  Alert,
  Divider
} from "antd";
import http from "@/utils/http";
import api from "@/utils/api";
import dayjs from "dayjs";
import { convertArrayToDayjs } from "@/utils/date";
import { HomeOutlined,CalendarOutlined } from "@ant-design/icons";
const { Text } = Typography;
const { RangePicker } = DatePicker;

const LeaveModal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [leaveData, setLeaveData] = useState(null);
  const [form] = Form.useForm();
  const [leaveType, setLeaveType] = useState("");
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [leaveDateRange, setLeaveDateRange] = useState(null);
  const [showTimeStart, setShowTimeStart] = useState(false);
  const [showTimeEnd, setShowTimeEnd] = useState(false);
  const [errorData, setErrorData] = useState(null);
  useImperativeHandle(ref, () => ({
    openModalFn: (type, ) => {
      setLeaveType(type);
      // setCurrentEmployeeId(employeeId);
      setVisible(true);
      fetchLeaveData(type, );
    },
    closeModalFn: () => {
      setVisible(false);
      resetForm();
    },
  }));

  // 转义
  const getTypeName = (type) => {
    switch (type) {
      case 1:
        return "Clock In";
      case 2:
        return "Sick Leave";
      case 3:
        return "Annual Leave";
      default:
        return "Unknown";
    }
  };
  // 计算时长（返回分钟，避免浮点数）
  const calculateDurationInMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    return end.diff(start, "minute");
  };

  // 分钟转小时（用于显示）
  const minutesToHours = (minutes) => {
    return Math.round((minutes / 60) * 100) / 100;
  };

  // 限制输入只能为数字和小数点   限制小数位数为1位
const sanitizeNumberInput = (value) => {
  if (!value) return "";
  let cleaned = value.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  if (parts.length === 2 && parts[1].length > 1) {
    cleaned = parts[0] + "." + parts[1].substring(0, 1);
  }
  return cleaned;
};

  // 验证时长
  const validateDuration = async (durationValue, timeRange, slotName) => {
    if (!durationValue && durationValue !== 0) {
      return;
    }
    const value = parseFloat(durationValue);
    const remainingHours = getRemainingHours(); // 获取剩余小时数

    // 使用剩余小时数作为上限  剩余时长≥ 8小时 上限为8  ＜8 默认和上限为剩余时长。
    if (value > remainingHours) {
      throw new Error(`Duration cannot exceed ${remainingHours} hours`);
    }

    if (isNaN(value)) {
      throw new Error("Please enter a valid number");
    }

    if (value <= 0) {
      throw new Error("Duration must be greater than 0");
    }
    // 时长在 0-8 之间时，必须选择时间范围
    if (
      value > 0 &&
      value < 8 &&
      (!timeRange || !timeRange[0] || !timeRange[1])
    ) {
      throw new Error(`Please select time range for ${slotName}`);
    }

    if (timeRange && timeRange[0] && timeRange[1]) {
      const calculatedMinutes = calculateDurationInMinutes(
        timeRange[0],
        timeRange[1],
      );
      const calculatedHours = minutesToHours(calculatedMinutes);
      if (calculatedHours < value) {
        // 判断 timeRange 时长 < durationValue
        throw new Error(
       `Selected time range (${calculatedHours}h) is less than required duration (${value}h)`,
        );
      }
    }
  };

  const createDurationValidator = (slotName, getTimeRange) => {
    return async (_, value) => {
      const timeRange = getTimeRange();
      await validateDuration(value, timeRange, slotName);
    };
  };
  //  TimePicker 的 onChange 处理函数
const getTimePickerChangeHandler = (
  durationValue,
  fieldName,
  timeRangeFieldName,
) => {
  return (values) => {
    if (values && values[0] && values[1]) {
      const minutes = calculateDurationInMinutes(values[0], values[1]);
      const hours = minutesToHours(minutes);
      const requiredHours = parseFloat(durationValue);
      
      // 设置时间范围
      form.setFieldsValue({ [timeRangeFieldName]: values });
      if (requiredHours && hours < requiredHours) {
        form.validateFields([fieldName]);
      } else {
        form.validateFields([fieldName]);
      }
    } else {
      form.setFieldsValue({ [timeRangeFieldName]: null });
      form.validateFields([fieldName]);
    }
  };
};
  // 获取请假数据

  const fetchLeaveData = async (type) => {
  setLoading(true);
  try {
    const typeValue = type === "annualLeave" ? 1 : 2;
    const res = await http.get(api.getEmployeeIdLeave, {
      params: {
        type: typeValue,
      },
    });
    if (res.code === "1") {
      setLeaveData(res.data);
      const minDate = convertArrayToDayjs(res.data.startTime);
      const maxDate = convertArrayToDayjs(res.data.endTime);
      const today = dayjs();
      
      // 检查今天是否在允许范围内
      let startDate = today;
      let endDate = today;
      
      if (minDate && today.isBefore(minDate, "day")) {
        // 如果今天早于最小日期，使用最小日期
        startDate = minDate;
        endDate = minDate;
      }
      if (maxDate && today.isAfter(maxDate, "day")) {
        // 如果今天晚于最大日期，使用最大日期
        startDate = maxDate;
        endDate = maxDate;
      }
      
      setLeaveDateRange([startDate, endDate]);
      
      let defaultDuration = 8;
      if (res.data.remainingHours && parseFloat(res.data.remainingHours) < 8) {
        defaultDuration = parseFloat(res.data.remainingHours);
      }
      
      form.setFieldsValue({
        leaveCycle: [startDate, endDate],
        durationStart: defaultDuration,
        durationEnd: defaultDuration,
        timeRangeStart: null,
        timeRangeEnd: null,
        remark: "",
        employeeId: res.data.employeeId,
      });
    } else {
      message.error(res.data.msg );
    }
  } catch (error) {
    message.error("Failed to get leave data, please try again");
  } finally {
    setLoading(false);
  }
};

  // 重置表单
  const resetForm = () => {
    form.resetFields();
    setLeaveData(null);
    setCurrentEmployeeId(null);
    setLeaveType("");
    setLeaveDateRange(null);
    setShowTimeStart(false);
    setShowTimeEnd(false);
    setErrorData(null);
  };

  // 获取剩余可用时长
  const getRemainingHours = () => {
  if (!leaveData) return 0;
  const hours = leaveData.remainingHours;
  return typeof hours === 'number' ? hours : parseFloat(hours) || 0;
  };
const isFormDisabled = getRemainingHours() === 0;
  // 构建时间段
  const buildTimeSlot = (date, duration, timeRange, dateLabel) => {
    if (!duration || duration <= 0) return null;

    let startDateTime, endDateTime;

    if (duration === 8) {
      // 8小时：一整天 00:00:00 到 23:59:59
      startDateTime = dayjs(date)
        .hour(0)
        .minute(0)
        .second(0)
        .format("YYYY-MM-DD HH:mm:ss");
      endDateTime = dayjs(date)
        .hour(23)
        .minute(59)
        .second(59)
        .format("YYYY-MM-DD HH:mm:ss");
    } else if (timeRange && timeRange[0] && timeRange[1]) {
      // 小于8小时：使用选择的时间段
      startDateTime = dayjs(date)
        .hour(timeRange[0].hour())
        .minute(timeRange[0].minute())
        .second(0)
        .format("YYYY-MM-DD HH:mm:ss");
      endDateTime = dayjs(date)
        .hour(timeRange[1].hour())
        .minute(timeRange[1].minute())
        .second(0)
        .format("YYYY-MM-DD HH:mm:ss");
    } else {
      // 小于8小时但没有选择时间段，报错
      message.error(
        `Please select time range for ${dayjs(date).format("MM/DD/YYYY")}`,
      );
      return null;
    }

    return {
      startTime: startDateTime,
      endTime: endDateTime,
      leaveDuration: duration,
    };
  };

  // 提交请假申请
  const handleSubmit = async () => {
    if (submitLoading) return;
    try {
       setSubmitLoading(true);
      await form.validateFields();
      const formValues = form.getFieldsValue();
      const startDate = formValues.leaveCycle[0];
      const endDate = formValues.leaveCycle[1];
  
      const list = [];
      // 处理开始日期
      const startSlot = buildTimeSlot(
        startDate,
        formValues.durationStart,
        formValues.timeRangeStart,
        "start",
      );
      if (startSlot) list.push(startSlot);

      // 处理结束日期（如果开始日期和结束日期不同）
      if (!dayjs(startDate).isSame(endDate, "day")) {
        const endSlot = buildTimeSlot(
          endDate,
          formValues.durationEnd,
          formValues.timeRangeEnd,
          "end",
        );
        if (endSlot) list.push(endSlot);
      }

      if (list.length === 0) return;

     
      // 获取整体时间范围
      const allStartTimes = list.map((item) => item.startTime);
      const allEndTimes = list.map((item) => item.endTime);
      const overallStartTime = [...allStartTimes].sort()[0];
      const overallEndTime = [...allEndTimes].sort().reverse()[0];

      const params = {
        employeesId: formValues.employeeId,
        startTime: overallStartTime,
        endTime: overallEndTime,
        remark: formValues.remark || "",
        list: list,
        // employeeId: formValues.employeeId,
      };
      // console.log('params:', params);
      const apiUrl =
        leaveType === "annualLeave"
          ? api.getInsertAnnualLeave
          : api.getInsertSickLeave;
      const res = await http.post(apiUrl, params);

      if (res.code === "1") {
        message.success("Leave application submitted successfully");
        setVisible(false);
        resetForm();
        // 回调刷新列表
         if (props.submitInitFn) {
    props.submitInitFn(props.currentMonth);
  }
      } else {
        if (res.data && res.data.length > 0) {
          setErrorData(res.data);
        } else {
          message.error(res.msg || "Submission failed, please try again");
        }
      }
    } catch (error) {
      console.error("Failed to submit leave application:", error);
      message.error("Submission failed, please try again");
    } finally {
      setSubmitLoading(false);
    }
  };
  // 取消
  const handleCancel = () => {
    setVisible(false);
    resetForm();
  };

  const durationStartValue = Form.useWatch("durationStart", form);
  const durationEndValue = Form.useWatch("durationEnd", form);

  return (
    <Modal
      // title={leaveType === "annualLeave" ? "Annual Leave" : "Sick Leave"}
        title={
    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#0E172B;" }}>
      <CalendarOutlined style={{ marginRight: "8px" }} />
      {leaveType === "annualLeave" ? "Annual Leave" : "Sick Leave"}
      {/* <div style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}></div> */}
       <Divider style={{ marginTop: 12, marginBottom: 0 }} />
    </div>
  }
      open={visible}
      onCancel={handleCancel}
      maskClosable={false}
      width="850px"
      footer={
        errorData
          ? null
          : [
              <Flex key="footer" justify="center" gap={12}>
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  key="submit"
                  type="primary"
                  loading={submitLoading}
                  onClick={handleSubmit}
                  disabled={isFormDisabled}
                >
                  Submit
                </Button>
              </Flex>,
            ]
      }
    >

      {errorData ? (
        // 显示错误信息
        <div>
          <Alert
            message="Submission Failed"
            description="The leave time you submitted conflicts with existing records. Please modify and apply again."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          {(() => {
            // 构建表格数据
            const tableData = errorData.map((item) => {
              const date = item.recordDate
                ? dayjs(
                    `${item.recordDate[0]}-${item.recordDate[1]}-${item.recordDate[2]}`,
                  ).format("MM/DD/YYYY")
                : "";
              const startTime = item.startTime
                ? dayjs(
                    `${item.startTime[0]}-${item.startTime[1]}-${item.startTime[2]} ${item.startTime[3]}:${item.startTime[4]}`,
                  ).format("HH:mm")
                : "";
              const endTime = item.endTime
                ? dayjs(
                    `${item.endTime[0]}-${item.endTime[1]}-${item.endTime[2]} ${item.endTime[3]}:${item.endTime[4]}`,
                  ).format("HH:mm")
                : "";

              return {
                key: item.id,
                date: date,
                workTime: `${startTime} – ${endTime}`,
                workDescription: item.remark || " ",
                workHours: `${minutesToHours(item.totalMinutes)}h`,
                type: getTypeName(item.type),
              };
            });

            return (
              <Table
                dataSource={tableData}
                className="error-table"
           columns={[
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "20%",
      align: "center",
    },
    {
      title: "Work Time",
      dataIndex: "workTime",
      key: "workTime",
      width: "25%",
      align: "center",
    },
    {
      title: "Work Hours",
      dataIndex: "workHours",
      key: "workHours",
      width: "15%",
      align: "center",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "20%",
      align: "center",
    },
    {
      title: "Work Description",
      dataIndex: "workDescription",
      key: "workDescription",
      width: "20%",
      align: "center",
    },
  ]}
                size="small"
                pagination={false}
                bordered={false}
                style={{ border: "none",
                  // overflowX: "visible" 
                 }}
              />
            );
          })()}

          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
          >
            <Button
              type="primary"
              onClick={() => {
                setErrorData(null);
              }}
            >
              OK
            </Button>
          </div>
        </div>
      ) : (
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            initialValues={{
              remark: "",
            }}
             disabled={isFormDisabled}
          >
            <Form.Item
              label={
                leaveType === "annualLeave"
                  ? "Remaining Annual Leave"
                  : "Remaining Sick Leave"
              }
            >
              <span style={{ fontSize: 18, color: "#2463EA", fontWeight: 500 }}>
                {getRemainingHours()} hours
              </span>
            </Form.Item>

            {/* Leave Cycle */}
            <Form.Item
              label="Leave Cycle"
              name="leaveCycle"
              rules={[{ required: true, message: "Please select leave cycle" }]}
            >
              <RangePicker
                format="MM/DD/YYYY"
                style={{ width: "300px" }}
                placeholder={["Start Date", "End Date"]}
                disabled={loading || isFormDisabled}
                disabledDate={(current) => {
                  if (!leaveData) return false;
                  const minDate = convertArrayToDayjs(leaveData.startTime);
                  const maxDate = convertArrayToDayjs(leaveData.endTime);
                  if (!minDate || !maxDate) return false;
                  return (
                    current &&
                    (current < minDate.startOf("day") ||
                      current > maxDate.endOf("day"))
                  );
                }}
                onChange={(dates) => {
                  form.setFieldsValue({ leaveCycle: dates });
                  setLeaveDateRange(dates);
                  setShowTimeStart(false);
                  setShowTimeEnd(false);
                }}
              />
            </Form.Item>
            {/* Leave Time - 开始日期 */}
            <Form.Item label="Leave Time" required>
              <div>
                {leaveDateRange && leaveDateRange[0] && (
                  <div
                    style={{
                      marginBottom: 24,
                      padding: "12px",
                      background: "#fafafa",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontWeight: 500, minWidth: "100px" }}>
                        {dayjs(leaveDateRange[0]).format("MM/DD/YYYY")}
                      </div>
                      <Form.Item
                        name="durationStart"
                        noStyle
                        rules={[
                          {
                            validator: createDurationValidator(
                              `Start Date ${dayjs(leaveDateRange[0]).format("MM/DD/YYYY")}`,
                              () => form.getFieldValue("timeRangeStart"),
                            ),
                          },
                        ]}
                      >
                        <Input
                          type="text"
                          placeholder="Duration"
                          style={{ width: "120px" }}
                          suffix="hours"
                          onChange={(e) => {
                            let value = e.target.value;
                            if (value !== "" && !/^\d*\.?\d{0,1}$/.test(value)) {
                            return;   }
                          form.setFieldsValue({ durationStart: value });
                          }}
                          onBlur={(e) => {
                            let value = e.target.value;
                            const cleaned = sanitizeNumberInput(value);
                            let numValue = parseFloat(cleaned) || 0;
                            // 使用剩余小时数作为上限
                            const remainingHours = getRemainingHours();
                            const maxLimit =
                              remainingHours < 8 ? remainingHours : 8;
                            numValue = Math.min(numValue, maxLimit);
                            form.setFieldsValue({ durationStart: numValue });
                            //  如果时长变为 8，清空时间范围
                            if (numValue === 8) {
                              form.setFieldsValue({
                                durationStart: numValue,
                                timeRangeStart: null,
                              });
                            } else {
                              form.setFieldsValue({ durationStart: numValue });
                            }
                            form.validateFields(["durationStart"]);
                          }}
                        />
                      </Form.Item>
                      {durationStartValue !== undefined &&
                        durationStartValue !== null &&
                        parseFloat(durationStartValue) < 8 &&
                        parseFloat(durationStartValue) !== 0 && (
                          <Form.Item name="timeRangeStart" noStyle>
                            <TimePicker.RangePicker
                              format="hh:mm A"
                              use12Hours
                              minuteStep={1}
                              placeholder={["Start", "End"]}
                              style={{ width: "260px" }}
                              onChange={getTimePickerChangeHandler(
                                durationStartValue,
                                "durationStart",
                                "timeRangeStart",
                              )}
                            />
                          </Form.Item>
                        )}
                    </div>
                  </div>
                )}

                {/* Leave Time - 结束日期（不是同一天） */}
                {leaveDateRange &&
                  leaveDateRange[1] &&
                  !dayjs(leaveDateRange[0]).isSame(
                    leaveDateRange[1],
                    "day",
                  ) && (
                    <div
                      style={{
                        padding: "12px",
                        background: "#fafafa",
                        borderRadius: "6px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ fontWeight: 500, minWidth: "100px" }}>
                          {dayjs(leaveDateRange[1]).format("MM/DD/YYYY")}
                        </div>
                        <Form.Item
                          name="durationEnd"
                          noStyle
                          rules={[
                            {
                              validator: createDurationValidator(
                                `End Date ${dayjs(leaveDateRange[1]).format("MM/DD/YYYY")}`,
                                () => form.getFieldValue("timeRangeEnd"),
                              ),
                            },
                          ]}
                        >
                          <Input
                            type="text"
                            placeholder="Duration"
                            style={{ width: "120px" }}
                            suffix="hours"
                            onChange={(e) => {
                            let value = e.target.value;
                             if (value !== "" && !/^\d*\.?\d{0,1}$/.test(value)) {
                                 return;
                              }
                            form.setFieldsValue({ durationEnd: value });
                            }}
                            onBlur={(e) => {
                              let value = e.target.value;
                              const cleaned = sanitizeNumberInput(value);
                              let numValue = parseFloat(cleaned) || 0;
                              // 使用剩余小时数作为上限
                              const remainingHours = getRemainingHours();
                              const maxLimit =
                                remainingHours < 8 ? remainingHours : 8;
                              numValue = Math.min(numValue, maxLimit);
                              form.setFieldsValue({ durationEnd: numValue });
                              if (numValue === 8) {
                                form.setFieldsValue({
                                  durationEnd: numValue,
                                  timeRangeEnd: null,
                                });
                              } else {
                                form.setFieldsValue({ durationEnd: numValue });
                              }
                              form.validateFields(["durationEnd"]);
                            }}
                          />
                        </Form.Item>
                        {/*  控制结束日期的时间选择器 */}

                        {durationEndValue !== undefined &&
                          durationEndValue !== null &&
                          parseFloat(durationEndValue) < 8 &&
                          parseFloat(durationEndValue) !== 0 && (
                            <Form.Item name="timeRangeEnd" noStyle>
                              <TimePicker.RangePicker
                                format="hh:mm A"
                                use12Hours
                                minuteStep={1}
                                placeholder={["Start", "End"]}
                                style={{ width: "260px" }}
                                onChange={getTimePickerChangeHandler(
                                  durationEndValue,
                                  "durationEnd",
                                  "timeRangeEnd",
                                )}
                              />
                            </Form.Item>
                          )}
                      </div>
                    </div>
                  )}

                {(!leaveDateRange || !leaveDateRange[0]) && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#999",
                      padding: "20px",
                    }}
                  >
                    Please select leave cycle first{" "}
                  </div>
                )}
              </div>
            </Form.Item>

            {/* Leave Description */}
            <Form.Item label="Leave Description" name="remark">
              <Input.TextArea
                rows={3}
                placeholder="Fill In"
                maxLength={200}
                showCount
              />
            </Form.Item>

            <Form.Item name="employeeId">
              <input hidden></input>
            </Form.Item>
          </Form>
        </Spin>
      )}
    </Modal>
  );
});

export default LeaveModal;
